import { Injectable, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { AppErrors } from '@fitness-force/models';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { formatApiErrorMessage } from './api-error-message.util';

export { formatApiErrorMessage } from './api-error-message.util';

export function extractApiErrorMessages(error?: AppErrors | null): string[] {
  const errorData = error?.originalError?.error;

  if (Array.isArray(errorData)) {
    return errorData
      .map((item: { errorMessage?: string }) => formatApiErrorMessage(item?.errorMessage))
      .filter((message): message is string => !!message);
  }

  if (typeof errorData === 'object' && errorData?.errorMessage) {
    const message = formatApiErrorMessage(errorData.errorMessage);
    return message ? [message] : [];
  }

  if (typeof errorData === 'string') {
    const message = formatApiErrorMessage(errorData);
    return message ? [message] : [];
  }

  return [];
}

@Injectable({
  providedIn: 'root'
})
export class ErrorMessageService {
  private readonly translateService = inject(TranslateService);

  /**
   * Returns error message as an Observable<string>
   * Handles various error structures and translates messages when needed
   */
  returnErrorMessage(error: any): Observable<string> {
    // Check if error has the expected structure
    if (!error?.originalError) {
      return of(
        error?.errorMessage ||
          error?.message ||
          error?.error?.errorMessage ||
          error?.error ||
          'An unexpected error occurred'
      );
    }

    const { status, error: errorData } = error.originalError;

    // Handle specific HTTP status codes
    switch (status) {
      case 403:
        return this.translateService.get('common.errors.validations.FORBIDDEN');
      
      case 404:
        return this.translateService.get('common.errors.validations.NOTFOUND');
      
      case 200:
        // Status 200 but treated as error
        return of(errorData?.text || 'Success');
      
      case 500:
        return this.translateService.get('common.errors.SERVER_SIDE.SOMETHING_WRONG');
      
      default:
        return of(this.extractErrorMessage(errorData));
    }
  }

  /**
   * Extracts error message from various error data structures
   */
  private extractErrorMessage(errorData: any): string {
    // Handle array of errors - return first error message
    if (Array.isArray(errorData)) {
      if (errorData.length > 0 && errorData[0]?.errorMessage) {
        return formatApiErrorMessage(errorData[0].errorMessage) ?? 'Multiple errors occurred';
      }
      return 'Multiple errors occurred';
    }
    
    // Handle string error
    if (typeof errorData === 'string') {
      return formatApiErrorMessage(errorData) ?? errorData;
    }
    
    // Handle object with errorMessage property
    if (errorData?.errorMessage) {
      return formatApiErrorMessage(errorData.errorMessage) ?? errorData.errorMessage;
    }
    
    // Fallback
    return 'An unexpected error occurred';
  }

  /**
   * Returns all error messages from an array of errors
   */
  returnAllErrorMessages(error: any): Observable<string[]> {
    const messages = extractApiErrorMessages(error);
    if (messages.length > 0) {
      return of(messages);
    }

    return this.returnErrorMessage(error).pipe(map((msg) => [msg]));
  }
}
